import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AnyMxRecord } from 'dns';
import { environment } from 'environments/environment';
import { httpHeader } from 'global/httpHeader';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { AuthService } from './auth/Auth.service';
import { ServiceCookie } from './cookie.service';
import { KeycloakAuthService } from './keycloak/keycloak-auth.service';
import { PermissionService } from './permission.service';
interface IMenuItem {
  type: string;
  name?: string;
  state?: string;
  icon?: string;
  tooltip?: string;
  disabled?: boolean;
  sub?: IChildItem[];
  badges?: IBadge[];
  style?: string;
  level?: number;
  S1?: number;
  S2?: number;
  id?: any;
}
interface IChildItem {
  type?: string;
  name: string;
  state?: string;
  icon?: string;
  sub?: IChildItem[];
  id?: any;
}
interface ICMenuItem {
  name?: string;
  type?: string;
  tooltip?: string;
  icon?: string;
  state?: string;
}
interface IBadge {
  color: string;
  value: string;
}

@Injectable({ providedIn: 'root' })
export class NavigationService {
  value: any;
  values: any;
  baseurl = environment.apiURL;
  baseurlIam = environment.apiUrlIAM;
  private readonly menuStorageKey = 'cached_menu_items_v2';
  private menuRequest: Promise<void> | null = null;
  private permissionsRequest: Promise<boolean> | null = null;
  private permissionsResult: boolean | null = null;
  iMenu?: {
    name: string;
    type: string;
    tooltip: string;
    icon: string;
    state: string;
    level: number;
    sub: IMenuItem[];
  };
  pendingList = [];
  helper = new JwtHelperService();
  constructor(
    private authService: AuthService,
    private http: HttpClient,
    private cookieService: ServiceCookie,
    private permissionService: PermissionService,
    private router: Router,
    private keycloakAuthService: KeycloakAuthService,
  ) {}

  public async initialize(forceRefresh = false): Promise<boolean> {
    const hasPermissions = await this.getPermissions(forceRefresh);

    if (hasPermissions) {
      this.loadMenu(forceRefresh);
    }

    return hasPermissions;
  }

  public loadMenu(forceRefresh = false): void {
    if (forceRefresh) {
      this.fetchMenu();
      return;
    }

    const cachedMenu = this.getCachedMenu();
    if (cachedMenu) {
      this.applyMenu(cachedMenu);
      return;
    }

    this.fetchMenu();
  }

  private getCachedMenu(): IMenuItem[] | null {
    const rawMenu = localStorage.getItem(this.menuStorageKey);
    if (!rawMenu) {
      return null;
    }

    try {
      return JSON.parse(rawMenu) as IMenuItem[];
    } catch (error) {
      console.warn('Invalid cached menu data, clearing storage.', error);
      localStorage.removeItem(this.menuStorageKey);
      return null;
    }
  }

  private setCachedMenu(menu: IMenuItem[]): void {
    localStorage.setItem(this.menuStorageKey, JSON.stringify(menu));
  }

  private applyMenu(menu: IMenuItem[]): void {
    this.iconMenu = menu;
    this.separatorMenu = [...menu];
    this.plainMenu = [...menu];
    this.menuItems.next(this.iconMenu);
  }

  private fetchMenu(): void {
    if (this.menuRequest) {
      return;
    }

    const request = lastValueFrom(
      this.http.get<any>(
        `${this.baseurlIam}menus/effective`,
        httpHeader.httpOptions,
      ),
    )
      .then((response) => {
        const menu = this.transformMockData(response.data || []);
        this.applyMenu(menu);
        this.setCachedMenu(menu);
      })
      .catch((error) => {
        console.error('Error fetching menu:', error);
        // this.keycloakAuthService.logout();
        this.router.navigate(['/forbidden-403']);
      })
      .finally(() => {
        if (this.menuRequest === request) {
          this.menuRequest = null;
        }
      });

    this.menuRequest = request;
  }

  private transformMockData(data: any[]): IMenuItem[] {
    return data.map((item) => this.transformMenuItem(item));
  }

  private transformMenuItem(item: any): IMenuItem {
    return {
      id: item.id,
      name: item.title,
      type: item.children && item.children.length > 0 ? 'dropDown' : 'link',
      icon: item.icon,
      state: item.path.startsWith('/') ? item.path.substring(1) : item.path,
      tooltip: item.title,
      sub: item.children ? this.transformChildItems(item.children) : undefined,
      badges: item.badge
        ? [{ color: 'primary', value: item.badge }]
        : undefined,
    };
  }

  private transformChildItems(data: any[]): IChildItem[] {
    return data.map((item) => ({
      id: item.id,
      name: item.title,
      type: item.children && item.children.length > 0 ? 'dropDown' : 'link',
      state: item.path.startsWith('/') ? item.path.substring(1) : item.path,
      icon: item.icon,
      sub: item.children ? this.transformChildItems(item.children) : undefined,
    }));
  }

  iconMenu: IMenuItem[] = [];

  separatorMenu: IMenuItem[] = [];

  plainMenu: IMenuItem[] = [];

  iconTypeMenuTitle = 'Frequently Accessed';
  // sets iconMenu as default;
  menuItems = new BehaviorSubject<IMenuItem[]>(this.iconMenu);
  // navigation component has subscribed to this Observable
  menuItems$ = this.menuItems.asObservable();

  publishNavigationChange(menuType: string) {
    switch (menuType) {
      case 'separator-menu':
        this.menuItems.next(this.separatorMenu);
        break;
      case 'icon-menu':
        this.menuItems.next(this.iconMenu);
        break;
      default:
        this.menuItems.next(this.plainMenu);
    }
  }
  async getPermissions(forceRefresh = false): Promise<boolean> {
    if (!forceRefresh && this.permissionsResult !== null) {
      return this.permissionsResult;
    }

    // Reuse the same promise when bootstrap, callback/loading, and dashboard
    // request permissions at the same time. A forced refresh must not create a
    // second request while one is already running.
    if (this.permissionsRequest) {
      return this.permissionsRequest;
    }

    const request = this.fetchPermissions();
    this.permissionsRequest = request;

    try {
      return await request;
    } finally {
      if (this.permissionsRequest === request) {
        this.permissionsRequest = null;
      }
    }
  }

  private async fetchPermissions(): Promise<boolean> {
    try {
      const response: any = await lastValueFrom(
        this.http.get<AnyMxRecord>(
          `${this.baseurlIam}authorization/effective?prefix=hb:hb-api-dms-depo:`,
          httpHeader.httpOptions,
        ),
      );

      const userPermissions = response.data || [];
      const permission: string[] = userPermissions
        .filter((perm: unknown): perm is string => typeof perm === 'string')
        .map((perm: string) => perm.split(':').slice(-2).join(':'))
        .filter((perm: string) => perm.includes(':') && perm.length > 2);

      if (permission.length === 0) {
        console.warn('User has no permissions, redirecting to forbidden page');
        this.permissionService.clearPermissions();
        this.permissionsResult = false;
        this.router.navigate(['/forbidden-403']);
        return false;
      }

      this.permissionService.setPermissions(permission);
      this.permissionsResult = true;
      return true;
    } catch (error) {
      this.permissionService.clearPermissions();
      this.permissionsResult = false;
      this.keycloakAuthService.login();
      console.error('Error fetching permissions:', error);
      return false;
    }
  }
  getMe(): Promise<any> {
    if (!httpHeader.token) {
      return Promise.resolve();
    }

    const existingId = localStorage.getItem('id');
    if (existingId && existingId !== '0') {
      return Promise.resolve(existingId);
    }

    return new Promise((resolve, reject) => {
      this.http
        .get(`${this.baseurl}user/me`, httpHeader.httpOptions)
        .subscribe({
          next: (response: any) => {
            const bpId = response?.data?.bpIds?.[0] || 0;
            localStorage.setItem('id', bpId);
            resolve(bpId);
          },
          error: (error) => {
            console.error('Error fetching menu:', error);
            // this.keycloakAuthService.logout();
            this.router.navigate(['/forbidden-403']);
            reject(error);
          },
        });
    });
  }
}
